import { Fragment, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useCart, useDeliveryDate, useMeals, useSettings } from '@root/MyChoice/context';
import dayjs from '@root/lib/packages/dayjs';
import CircleBox from '@root/lib/components/Icons/CircleBox';
import Calendar from '@root/lib/components/Icons/Calendar';
import Portal from '@root/lib/components/Portal';
import ArrowLeft from '@root/lib/components/Icons/ArrowLeft';
import ArrowRight from '@root/lib/components/Icons/ArrowRight';
import Slider from '@root/lib/components/Slider';
import Spinner from '@root/lib/components/Spinner';
import MealCard from '@root/lib/components/MealCard';
import useAsyncEffect from '@root/lib/hooks/useAsyncEffect';
import useAsyncCallback from '@root/lib/hooks/useAsyncCallback';
import Question from '@root/lib/components/Icons/Question';
import Alert from '@root/lib/components/Alert';
import * as RecommendedService from '@root/lib/services/RecommendedService';
import * as KlayvioService from '@root/lib/services/KlayvioService';
import * as LogicService from '@root/lib/services/LogicService';
import { ATCButton, Controls, Filters } from './components';
import { useFilters } from './hooks';
import cn from 'classnames';
import './Grid.scss';

const sliderBreakpoints = [
  {
    width: 1660,
    settings: {
      slidesToShow: 3,
    }
  },
  {
    width: 1280,
    settings: {
      slidesToShow: 2,
    }
  },
  {
    width: 1090,
    settings: {
      slidesToShow: 2,
    }
  },
  {
    width: 1024,
    settings: {
      slidesToShow: 3,
    }
  },
  {
    width: 810,
    settings: {
      slidesToShow: 2,
    }
  },
  {
    width: 620,
    settings: {
      slidesToShow: 1,
    }
  },
];

/**
 * Checks if the customer is logged in or not
 */
const isLoggedIn = () => {
  return Shopify?.customer ? true : false;
}

/**
 * Checks if a customer has the given tag. If the customer is not logged in
 * false will be returned
 *
 * @param {string} tag
 */
const customerHasTag = (tag) => {
  return Shopify?.customer?.tags?.includes(tag) ?? false;
}

const Grid = () => {
  const [ meals, mealsApi ] = useMeals();
  const [ settings ] = useSettings();
  const [ deliveryDate ] = useDeliveryDate();
  const [ bestSellerIds, setBestSellerIds ] = useState([]);
  const [ cart, cartApi ] = useCart();
  const [ sliderControlFlags, setSliderControlFlags ] = useState({next: false, prev: false, slideStatuses: [], slidesInView: 0});
  const [ showOmitRoboAdamAlert, setShowOmitRoboAdamAlert ] = useState(() => customerHasTag('omit_roboadam'));
  const [ isMobile, setIsMobile ] = useState(window.screen.availWidth < 769);
  const sliderRef = useRef();
  const filters = useFilters(meals);
  const sectionedMeals = useMemo(() => {
    const sections = new Map();

    for (const meal of filters.meals) {
      const section = sections.get(meal.type) ?? [];
      sections.set(meal.type, section);
      section.push(meal);
    }

    return Array.from(sections.entries()).sort((a, b) => {
      const { sectionSorting } = settings;
      const [ aSectionName ] = a;
      const [ bSectionName ] = b;
      let aPosition = sectionSorting.findIndex(sectionName => sectionName === aSectionName);
      let bPosition = sectionSorting.findIndex(sectionName => sectionName === bSectionName);
      aPosition = aPosition === -1 ? 255 : aPosition;
      bPosition = bPosition === -1 ? 255 : bPosition;

      const delta = aPosition - bPosition;
      return delta !== 0
        ? delta
        : aSectionName.localeCompare(bSectionName);
    });
  }, [filters.meals, settings.sectionSorting]);
  const bestSellers = useMemo(() => {
    const bestSellerProductIds = new Set(bestSellerIds.map(b => b.product_id));
    return Array.from(bestSellerProductIds).reduce((meals, id) => {
      const meal = filters.meals.find(m => m.id === id);
      meal && meals.push(meal);
      return meals;
    }, []);
  }, [filters.meals, bestSellerIds]);
  const formattedDeliveryDate = useMemo(() => {
    return !deliveryDate ? '' : dayjs(deliveryDate).tz().format('dddd, MMMM D');
  }, [deliveryDate]);
  const menuId = LogicService.calculateMenuId(deliveryDate);

  /**
   * Adds an item to the local cart
   *
   * @type {(variantId: number, quantity?: number) => void}
   */
  const addItemToCart = useCallback((variantId, quantity = 1) => {
    const gid = btoa(`gid://shopify/ProductVariant/${variantId}`);

    cartApi.addItems({
      quantity,
      variantId: gid,
    }).then(() => {
      const params = new URLSearchParams(location.search);
      if(params.has('discount')){
        return cartApi.setDiscountCode(params.get('discount'));
      }
    }).then(() => {
      const variants = meals.flatMap(meal => meal.variants);
      const variant = variants.find(variant => variant.id === variantId);
      KlayvioService.track('Added to Cart', {
        Price: variant.price / 100,
        PriceCents: variant.price,
        Name: `${variant.product.title} - ${variant.title}`,
        ImageURL: variant.images.mediumUrl,
        ProductID: variant.product.id,
        VariantID: variant.id,
        CartID: cart.id,
        Type: variant.product.type,
        SKU: variant.sku,
        Brand: variant.product.vendor,
        URL: `https://${location.host}/products/${variant.product.handle}`,
      });
    });
  }, [meals]);

  /**
   * Updates an existing item in the cart's quantity
   *
   * @type {(
   *   lineItemId: string,
   *   quantity: number,
   * ) => Promise<void>}
   */
  const updateItemQuantity = useAsyncCallback(async (isCancelled, lineItemId, quantity) => {
    return await cartApi.updateItems({
      id: lineItemId,
      quantity,
    });
  }, []);

  /**
   * Adds the quantity in cart of the provided variant to the end of the title
   *
   * @type {(variant: Object) => string}
   */
  const addQtyToVariantTitle = useCallback((variant) => {
    const title = variant.options[0].value.split('-')[0].trim();
    const qtyInCart = (cart.lineItems ?? []).reduce((total, li) => {
      return total + (li.variant.id !== variant.id ? 0 : li.quantity);
    }, 0);

    return qtyInCart === 0 ? title : `${title} (${qtyInCart})`;
  }, [cart.lineItems])

  /**
   * Handles events from the slider when certain controls change state
   *
   * @type {(
   *   next: boolean,
   *   prev: boolean,
   *   extra: {
   *     slideStatuses: Array<boolean>;
   *   },
   * ) => void}
   */
  const handleScrollChange = useCallback((next, prev, extra) => {
    setSliderControlFlags({
      next,
      prev,
      slideStatuses: extra.slideStatuses,
      slidesInView: extra.slideStatuses.reduce((total, b) => total + b, 0),
    });
  }, []);

  // Getting the best seller IDs/recommended
  useAsyncEffect(async (isCancelled) => {
    if (
      !deliveryDate ||
      (settings.isReturn && !settings.showBestSellersReturnFlow) ||
      (!settings.isReturn && !settings.showBestSellersInitialFlow)
    ) {
      setBestSellerIds([]);
      return;
    }

    const customerId = Shopify.customer?.id;
    const subscriptionId = settings.subscription?.bold_platform_subscription_id;
    const recommendations = await RecommendedService.getRecommendedProducts(menuId, customerId, subscriptionId);
    !isCancelled() && setBestSellerIds(recommendations.recommendedProducts);
  }, [menuId, settings.subscription?.bold_platform_subscription_id,
    settings.isReturn, settings.showBestSellersInitialFlow, settings.showBestSellersReturnFlow]);

  // Listening for screen resize changes and updating mobile state if threahold goes above
  // or below
  useLayoutEffect(() => {
    const listener = () => {
      const width = (window.innerWidth > 0) ? window.innerWidth : screen.width;
      setIsMobile(width < 811 || window.matchMedia('(hover: none)').matches);
    }

    listener();
    window.addEventListener('resize', listener);
    return () => window.removeEventListener('resize', listener);
  }, []);
  return (
    <div className="MealSelectionGrid">
      <div className="MealSelectionGrid__Inner">
        {mealsApi.isLoading() ? (
          <Spinner />
        ): meals.length === 0 ? (
          <h2 className="MealSelectionGrid__NoMeals">No Meals</h2>
        ) : (
          <>
             {showOmitRoboAdamAlert && (
              <Alert className="MealSelectionGrid__Alert" variation="big" title={Shopify.language.my_choice.omit_roboadam_alert_title} onDismiss={() => setShowOmitRoboAdamAlert(false)}>
                <span dangerouslySetInnerHTML={{__html: Shopify.language.my_choice.omit_roboadam_alert}} />
              </Alert>
            )}
            <header
              className={cn({
                'MealSelectionGrid__Header': true,
                'MealSelectionGrid__Header__ReturnFlow': settings.isReturn,
              })}
            >
              {settings.isReturn ? (
                <>
                  <Portal element="#header-extra">
                    <div className="MealSelectionGrid__StatusDelivery MealSelectionGrid__StatusDelivery--mobile">
                      <h4 className="MealSelectionGrid__Status">
                        {customerHasTag('omit_roboadam') ? 'Available' : 'Upcoming'}  
                      </h4>
                      <div className="MealSelectionGrid__Delivery">
                        Delivery on
                        <Calendar style={{position: 'relative', top: '2px'}} />{' '}
                        <strong style={{marginRight: '10px'}}>{formattedDeliveryDate}</strong>
                      </div>
                    </div>
                  </Portal>
                  <div className="MealSelectionGrid__StatusDelivery">
                    <CircleBox className="MealSelectionGrid__StatusDeliveryIcon" />
                    <h4 className="MealSelectionGrid__Status">
                      {customerHasTag('omit_roboadam') ? 'Available' : 'Upcoming'}  
                    </h4>
                    <div className="MealSelectionGrid__Delivery">
                      Delivery on
                      <Calendar style={{position: 'relative', top: '2px'}} />{' '}
                      <strong style={{marginRight: '10px'}}>{formattedDeliveryDate}</strong>
                      Meals arrive fresh - eat within <strong>5 - 7</strong> days or freeze for later
                    </div>
                  </div>
                </>
              ) : (
                <div>
                  <h4 className="MealSelectionGrid__Heading">Choose your meals</h4>
                  {settings.minMeals !== undefined && settings.maxMeals !== undefined && (
                    <p className="MealSelectionGrid__MinMaxMeals">
                      Select
                      <strong> {settings.minMeals} to {settings.maxMeals} </strong>
                      chef-prepared meals from a <strong>new menu every week.</strong> Change plans or cancel anytime.
                    </p>
                  )}
                </div>
              )}
              <Controls
                className="MealSelectionGrid__Controls"
                filters={filters.all}
                activeFilters={filters.active}
                onChange={filters.set}
                renderFilters={(open) => (
                  <Filters
                    filters={filters.all}
                    activeFilters={filters.active}
                    onChange={filters.set}
                    open={open}
                  />
                )}
              />
              <hr className="MealSelectionGrid__Rule" />
            </header>
            {bestSellers.length !== 0 && (
              <div className="MealSelectionGrid__BestSellers">
                <h4 className="MealSelectionGrid__BestSellersTitle">
                  {!settings.isReturn ?
                    'Best Sellers'
                  : isMobile ?
                    'Recommended'
                  : //else
                    'Recommended For You'
                  }
                </h4>
                <div className="MealSelectionGrid__SliderControls" style={{
                  '--dot-color-active': settings.dotColorActive,
                  '--dot-color-inactive': settings.dotColorInactive,
                  '--total-dots': sliderControlFlags.slideStatuses.length,
                }}>
                  {!settings.showDots || !isMobile ? <>
                    <button
                      onClick={() => sliderRef.current.prev()}
                      className="MealSelectionGrid__SliderControl MealSelectionGrid__SliderControl--left"
                      disabled={!sliderControlFlags.prev}
                      children={<ArrowLeft />}
                    />
                    <button
                      onClick={() => sliderRef.current.next()}
                      className="MealSelectionGrid__SliderControl MealSelectionGrid__SliderControl--right"
                      disabled={!sliderControlFlags.next}
                      children={<ArrowRight />}
                    />
                  </> : sliderControlFlags.slideStatuses.map((flag, idx) =>
                    <button
                      key={idx}
                      onClick={() => sliderRef.current.gotoSlide(idx, 'center')}
                      className={cn('MealSelectionGrid__SliderDot', {
                        'MealSelectionGrid__SliderDot--active': flag,
                      })}
                    />
                  )}
                </div>
                <Slider
                  className="MealSelectionGrid__Slider"
                  slidesToShow={4}
                  breakpoints={sliderBreakpoints}
                  onScrollChange={handleScrollChange}
                  ref={sliderRef}
                >
                  {bestSellers.map(meal =>
                    <MealCard
                      {...meal}
                      key={meal.id}
                      variantTitleFormatter={addQtyToVariantTitle}
                      action={(selectedVariant) => {
                        return (
                          <ATCButton
                            variant={selectedVariant}
                            disabled={cartApi.isLoading('addItems', 'updateItems')}
                            cartItems={cart.lineItems ?? []}
                            onAdd={addItemToCart}
                            onUpdate={updateItemQuantity}
                          />
                        );
                      }}
                    />
                  )}
                </Slider>
              </div>
            )}
            {sectionedMeals.map(([ section, meals ]) =>
              <Fragment key={section}>
                <h5 className="meals-header-entrees">{section}</h5>
                <div className="MealSelectionGrid__Meals">
                  {meals.map(meal =>
                    <MealCard
                      {...meal}
                      key={meal.id}
                      variantTitleFormatter={addQtyToVariantTitle}
                      action={(selectedVariant) => {
                        return (
                          <ATCButton
                            variant={selectedVariant}
                            disabled={cartApi.isLoading('addItems', 'updateItems')}
                            cartItems={cart.lineItems ?? []}
                            onAdd={addItemToCart}
                            onUpdate={updateItemQuantity}
                          />
                        );
                      }}
                    />
                  )}
                </div>
              </Fragment>
            )}
          </>
        )}
      </div>
      <Portal element="#header-extra">
        <p className="MealSelectionGrid__Marketing">
          Meals arrive fresh - eat within <strong>5 - 7</strong> days or freeze for later
        </p>
      </Portal>
    </div>
  );
}

export default Grid;
